import { Component, DestroyRef, Input, OnInit } from '@angular/core';
import { combineLatest, filter, lastValueFrom, map, Observable } from 'rxjs';
import { AlertEntry } from '../../../../development/alerts.service';
import { select, Store } from '@ngrx/store';
import * as GrantedAccessSelectors from '@states/granted-access/granted-access.selectors';
import { AppState } from '../../../../store/app.state';
import { CameraThumbnailsData, ThumbOptions } from '../../../../cameras/camera-thumbnails/camera-thumbnails.model';
import { MultiPlaybackData } from '@models/multi-playback.model';
import { MultiPlaybackActions } from '@states/multi-playback/multi-playback.action-types';
import { ThumbnailDialogData, ThumbnailDialogType } from '../../../../shared/thumbnail/thumbnail-dialog/thumbnail-dialog.component';
import { AnalyticClasses } from '@enums/alert-events.enum';
import { CamerasThumbnailsService } from '../../../../cameras/camera-thumbnails/camera-thumnails.service';
import { Archive, ArchiveStatus } from '@models/archive.model';
import ArchiveDocument = Archive.ArchiveDocument;
import * as LocationModel from '../../../../locations/location.model';
import { ofType } from '@ngrx/effects';
import { EdgeEditEffects } from '@effects/edge-edit.effects';
import { EdgeActions } from '@states/edge/edge.action-types';
import { VideoService } from '../../../../development/video.service';
import { GrantedAccessActions } from '@states/granted-access/granted-access.action-types';
import { SharedApiService } from '../../../../services/shared-api.service';
import { ThumbnailsService } from 'src/app/development/thumbnails.service';
import { StorageBucket } from '@consts/storage-url.util';
import * as OrganizationSelectors from '@states/organization/organization.selectors';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-shared-alert',
  templateUrl: './shared-alert.component.html',
  styleUrls: ['./shared-alert.component.scss'],
  standalone: false,
})
export class SharedAlertComponent implements OnInit {
  @Input() sharedArchive: ArchiveDocument;
  @Input() archiveUrl: string;
  @Input() accessToken: string;

  public selectSharedAlert$: Observable<AlertEntry> = this.store$.pipe(select(GrantedAccessSelectors.selectSharedAlert));
  public selectSelectedLocation$: Observable<LocationModel.LocationModel.LocationItem> = this.store$.pipe(select(GrantedAccessSelectors.selectSelectedLocation));
  public selectIsRecordRemoved$: Observable<boolean> = this.store$.pipe(select(GrantedAccessSelectors.selectIsRecordRemoved));

  public dialogData: ThumbnailDialogData;
  public archiveStatuses = ArchiveStatus;

  private edgeId: string;
  private cameraId: string;
  public online: boolean = false;

  constructor(
    private store$: Store<AppState>,
    private cameraThumbnailsService: CamerasThumbnailsService,
    private editEdgeEffects: EdgeEditEffects,
    private videoService: VideoService,
    private sharedApiService: SharedApiService,
    private thumbnailsService: ThumbnailsService,
    private destroyRef: DestroyRef,
  ) {
  }

  async ngOnInit(): Promise<void> {
    this.videoService.setLive(false);
    combineLatest([
      this.selectSharedAlert$.pipe(filter((alert) => !!alert)),
      this.selectSelectedLocation$.pipe(filter((location) => !!location)),
      this.store$.pipe(select(OrganizationSelectors.selectActiveOrganization)),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async ([alert, location, organization]) => {
        this.store$.dispatch(EdgeActions.getEdgesLastMp4Ts());

        this.edgeId = alert.edgeId;
        this.cameraId = alert.cameraId;
        await this.thumbnailsService.getGcpTransitionForEdge(this.edgeId);
        await lastValueFrom(this.thumbnailsService.whenPrivateStorageMapReady());

        this.store$.dispatch(GrantedAccessActions.getCameraAudio({ edgeId: this.edgeId, cameraId: this.cameraId }));
        this.editEdgeEffects.getEdgeHeartBeatStatus$
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            ofType(GrantedAccessActions.getEdgeHeartBeatStatusSuccess),
            map((res) => res.body),
          )
          .subscribe((edgeStatus) => {
            const cameraItem = edgeStatus.find((item) => item.component === 9 && item.edgeId === alert.edgeId);
            this.online = cameraItem?.status === 0;
          });
        const organizationName = organization?.orgName ?? '';
        this.loadPlayback(alert, location, organizationName);
      });
  }

  public openMail() {
    window.open(`mailto:sales@lumana.ai`);
  }

  public openLink(): void {
    window.open(this.archiveUrl, '_blank');
  }


  /**
   * WARNING SPAGHETTI
   */

  public loadPlayback(alert: AlertEntry, location: LocationModel.LocationModel.LocationItem, organizationName: string): void {
    const cameraName = this.getCameraNameFromLocation(location, alert.edgeId, alert.cameraId);
    //todo move to effect
    this.sharedApiService.getLocationTimezoneByEdgeId(this.edgeId)
      .subscribe(async res => {
        const timezone = res.timezone;
        const startTime = this.normalizeTimestamp(alert.timestamp - 40000);
        const endTime = this.normalizeTimestamp(Math.min(alert.timestamp + 40000, new Date().getTime()));

        this.videoService.timeLine = { start: startTime, end: endTime };
        const options: ThumbOptions = {
          clipInSeconds: 7200,
          offsetResInDurations: 1,
          startTime,
          endTime,
          duration: 2000,
        };
        const thumbsData: CameraThumbnailsData = {
          edgeId: alert.edgeId,
          cameraId: alert.cameraId,
          timezone: timezone,
          offsetResInDurations: 1,
        };

        const multiPlaybackData: MultiPlaybackData = {
          base: startTime,
          duration: 2000,
          endTime,
          offsetResInDurations: 1,
          startTime,
          timezone: timezone,
        };
        this.store$.dispatch(MultiPlaybackActions.setData({ data: multiPlaybackData }));

        this.cameraThumbnailsService.setThumbnailsData(thumbsData);

        this.dialogData = {
          options,
          events: null,
          edgeId: alert.edgeId,
          cameraId: alert.cameraId,
          selectedCamera: alert.selectedCamera,
          objects: alert?.thumbnails?.map(thumb => {
            return {
              url: thumb,
              idIndex: alert.idIndex,
              idBase: alert.idBase,
              descriptor: false,
              timestamp: alert?.timestamp,
            };
          }),
          type: ThumbnailDialogType.ALERT,
          rtl: false,
          defaultThumb: alert.mainThumbnail,
          showObjects: false,
          seconds: true,
          allowTrack: alert.idBase && alert.idIndex && alert.type === AnalyticClasses.person,
          alertName: alert.alertMessage,
          name: alert.eventName,
          shareName: organizationName,
          cameraName,
        };
      });
  }

  private getCameraNameFromLocation(
    location: LocationModel.LocationModel.LocationItem,
    edgeId: string,
    cameraId: string,
  ): string {
    const camera = location?.edges?.[edgeId]?.cameras?.[cameraId];
    return camera?.edgeOnly?.name ?? '';
  }


  normalizeTimestamp(timestamp: number) {
    return this.cameraThumbnailsService.normalizeTimestamp(timestamp);
  }

  getBaseInLocale(date: Date) {
    return this.cameraThumbnailsService.getBaseInLocale(date);
  }


  bestImageUriAws(object: string) {
    const base = this.thumbnailsService.getBucketBaseUrl(StorageBucket.AlertThumbnails, true, false, this.edgeId);
    return `${base}/alerts/${this.edgeId}/${this.cameraId}/${object}`;
  }

  bestImageUriGcp(object: string) {
    const base = this.thumbnailsService.getBucketBaseUrl(StorageBucket.AlertThumbnails, true, false, this.edgeId);
    return `${base}/alerts/${this.edgeId}/${this.cameraId}/${object}`;
  }

}
